import { isExcludedBrand } from "@/lib/brand-exclusions"

/**
 * The order of the "Explore Other Brands" and "Shop by Manufacturer" grids
 * (components/brand-kit/brand-logo-grid.tsx), in a plain module so the server
 * can pick the tiles a grid shows first (FRIDGE-1623: a product page sends the
 * ten it shows, not all of its partner brands) with the very order the grid
 * uses in the browser.
 */

/**
 * Canonical brand key — collapses the same real-world brand written many ways
 * into one identity so the grid never shows a brand twice. Lowercase, strip
 * accents, strip trailing generic corporate/format descriptors ("Appliances",
 * "Products", "Electronics", "Kitchen and Bath", "Advanced Appliances", "Inc",
 * "Co"…), then drop every non-alphanumeric char. This merges the three GE rows
 * the DB carries — "GE", "GE Appliances", "Geappliances" — into a single "ge",
 * plus "Summit"/"Summit Appliance" → "summit", "LG"/"LG Electronics" → "lg",
 * "ZLINE"/"ZLINE Kitchen and Bath" → "zline", "Avanti"/"Avanti Products" →
 * "avanti". Genuinely distinct sub-brand LINES keep their own key on purpose
 * ("GE Profile" → "geprofile", "Café" → "cafe", "Frigidaire Gallery" →
 * "frigidairegallery") — those are separate product families, not dupes.
 *
 * The collapsed forms this produces ARE the keys of BRAND_POPULARITY_RANK below
 * (that map was generated with the equivalent Postgres canon over the same
 * data); folding an extra long variant like "LG Electronics" onto "lg" simply
 * inherits LG's rank, which is what we want.
 */
export function canonicalBrandKey(name: string): string {
  const accents = "áàâäãéèêëíìîïóòôöõúùûüçñ"
  const plain = "aaaaaeeeeiiiiooooouuuucn"
  const deaccented = (name || "")
    .toLowerCase()
    .split("")
    .map((ch) => {
      const i = accents.indexOf(ch)
      return i === -1 ? ch : plain[i]
    })
    .join("")
  // Strip a trailing generic descriptor (multiword first). NOT "home"/"design"/
  // "professional"/"gallery" — those belong to real, distinct brand names.
  const desuffixed = deaccented.replace(
    /\s*(kitchen (and|&) bath|advanced appliances|appliances|appliance|electronics|products|product|refrigeration|refrigerators|refrigerator|international|company|inc|llc|co|usa)\s*$/,
    ""
  )
  return desuffixed.replace(/[^a-z0-9]/g, "")
}

/**
 * Popularity rank in America — most-popular first. Derived ENTIRELY from owned
 * data, never hand-judged, so it is honest and reproducible. Recipe (run against
 * the Fridge production Supabase, 07/20/2026):
 *
 *   composite = 0.6 * norm(per-brand gsc_impressions_28d)
 *             + 0.4 * norm(per-brand non-bot outbound clicks)
 *
 * gsc_impressions_28d: 28-day Search Console impressions from the
 * seo_url_metrics mirror, joined product-URL → brand (deduped MAX per URL).
 * Non-bot clicks: COUNT(*) from public.clicks joined clicks.product_id →
 * products.id with is_bot = false — real shoppers clicking out to retailers.
 * Each signal min-max normalized to 0..1, keys collapsed via the same canon()
 * as above, sorted composite DESC. Full generating SQL lives in the lane report.
 *
 * Why there is NO catalog-count or summed-score term: the earlier draft summed
 * per-product popularity_score per brand, which rewards catalog SIZE — a brand
 * with 2,000 mediocre SKUs beats one with 50 in-demand ones (that artifact put
 * Summit at #2 purely on its 2,260-SKU catalog). Both surviving terms are pure
 * per-brand consumer demand: people finding the products in search, and people
 * clicking through to buy them.
 *
 * Sanity check (owner gate): Whirlpool #1, Frigidaire #2, Samsung #3,
 * KitchenAid #4, GE #6, LG #8 — top-tier consumer brands lead. Data-driven
 * keeps, reported not hand-fixed: Summit #5 (17,827 non-bot clicks, 2nd-highest
 * on the site — genuine demand, not catalog size) and Demuller #7 (319 GSC
 * impressions + 1,011 real clicks on a long-tail import brand). Data wins.
 *
 * Brands absent from this map render after the ranked head, ordered alphabetically.
 */
export const BRAND_POPULARITY_RANK: Record<string, number> = Object.fromEntries(
  [
    "whirlpool", "frigidaire", "samsung", "kitchenaid", "summit", "ge",
    "demuller", "lg", "magicchef", "avanti", "blackdecker", "premiumlevella",
    "edgestar", "whynter", "danby", "midea", "antarcticstar", "amana",
    "kenmore", "upstreman", "euhomy", "feelfunn", "hamiltonbeach", "bosch",
    "accucold", "maytag", "vinturi", "procoolcreativecoolingsolutions",
    "geprofile", "hisense", "koolatron", "koolmore", "smeg", "igloo", "zline",
    "calefort", "electactic", "cafe", "orymuse", "erivess", "commercialcool",
    "zephyr", "avallon", "winia", "scotsman", "frigidairegallery", "element",
    "bodega", "arcticchef", "northair",
  ].map((k, i) => [k, i])
)

/**
 * The grid's order: junk brands out (lib/brand-exclusions.ts), one tile per
 * canonical brand (the shortest name, then alphabetical, so the merged GE row
 * is the same whatever order the database returns), most popular first, then
 * the rest alphabetically.
 */
export function orderBrandsForGrid<T extends { name: string; slug: string }>(brands: readonly T[]): T[] {
  const byKey = new Map<string, T>()
  for (const b of brands) {
    if (!b || !b.name) continue
    // Suppress non-appliance junk brands (beverages, entertainment IP, tire/
    // auto marques, parse-artifact tokens) that leaked in from novelty
    // products.
    if (isExcludedBrand({ slug: b.slug, name: b.name })) continue
    const key = canonicalBrandKey(b.name)
    if (!key) continue
    const existing = byKey.get(key)
    if (
      !existing ||
      b.name.length < existing.name.length ||
      (b.name.length === existing.name.length && b.name < existing.name)
    ) {
      byKey.set(key, b)
    }
  }
  return Array.from(byKey.values()).sort((a, b) => {
    const ra = BRAND_POPULARITY_RANK[canonicalBrandKey(a.name)] ?? Number.MAX_SAFE_INTEGER
    const rb = BRAND_POPULARITY_RANK[canonicalBrandKey(b.name)] ?? Number.MAX_SAFE_INTEGER
    if (ra !== rb) return ra - rb
    return a.name.localeCompare(b.name)
  })
}
